// 58 §4 — ONE event-type chip strip, mounted on every surface that shows the
// agenda: AgendaView, the case list, the collective list, and the Dashboard.
// Laurent, 2026-08-10: "adding dynamically when an event type is added in
// taxonomies, same colour."
//
// The rendering is AgendaView's, verbatim — the same sizes, the same tint at
// .18, the same JetBrains Mono, the same off-state at 40% opacity. What
// changed is where the chip set comes from (`src/lib/eventTypeFilter.ts`) and
// that four surfaces now share both the set and the predicate, so a type
// switched off on one is switched off on all of them: the filter has always
// lived in App as the persisted `agendaTypeFilter` preference.
//
// Module scope, and no hook — CLAUDE.md's Bug A. The Multi button is optional
// because only Agenda offers the mode today; the other three mount the chips
// alone and inherit whichever mode Agenda is in.
import { RAD, FS } from "../lib/tokens";
import type { TFn, EventTypeDisplay } from "../lib/taxonomyTypes";
import {
  eventTypeChips,
  isEventTypeOn,
  toggleEventType,
  chipTint,
  type EventTypeFilter,
} from "../lib/eventTypeFilter";

type Props = {
  /** the folded `event_type` display list — the chip set's source and its order */
  evtTypes: readonly EventTypeDisplay[] | null | undefined;
  /** the events in scope, read only to find codes the taxonomy no longer holds */
  events: readonly { type?: string | null }[] | null | undefined;
  value: EventTypeFilter;
  onChange: (next: Record<string, boolean> | null) => void;
  multi?: boolean;
  /** omit to render the chips without the Multi button */
  onMulti?: (() => void) | null;
  t: TFn;
  style?: React.CSSProperties;
};

export default function EventTypeChips({
  evtTypes,
  events,
  value,
  onChange,
  multi = false,
  onMulti = null,
  t,
  style,
}: Props) {
  const chips = eventTypeChips(evtTypes, events);
  if (chips.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 3,
        marginBottom: 4,
        flexWrap: "wrap",
        alignItems: "center",
        ...(style || {}),
      }}
    >
      {chips.map((c) => {
        const on = isEventTypeOn(value, c.code);
        return (
          <button
            key={c.code}
            // An orphan is named rather than dropped: the code is on real
            // events and the taxonomy no longer holds it. Dashed, so the eye
            // can tell it from a type the firm actually offers.
            title={
              c.orphan
                ? t("agenda.type.not_in_taxonomy", {
                    code: c.code,
                    fallback: "{code} — no longer in the event-type taxonomy",
                  })
                : c.label
            }
            style={{
              fontSize: FS.s8_5,
              padding: "1px 5px",
              borderRadius: RAD.r4,
              fontFamily: "'JetBrains Mono',monospace",
              fontWeight: 600,
              cursor: "pointer",
              border:
                (c.orphan ? "1px dashed " : "1px solid ") +
                (on ? "var(--br)" : "transparent"),
              background: on ? chipTint(c.color, 0.18) : "none",
              color: on ? c.color || "var(--ts)" : "var(--tm)",
              opacity: on ? 1 : 0.4,
            }}
            onClick={() => onChange(toggleEventType(value, c.code, chips, multi))}
          >
            {c.label}
          </button>
        );
      })}
      {onMulti && (
        <button
          style={{
            fontSize: FS.s8,
            padding: "1px 5px",
            borderRadius: RAD.r4,
            cursor: "pointer",
            border: "1px solid " + (multi ? "var(--ag)" : "var(--br)"),
            background: multi ? "var(--as)" : "none",
            color: multi ? "var(--ac)" : "var(--tm)",
            fontFamily: "inherit",
            marginLeft: 2,
          }}
          onClick={onMulti}
        >
          {t("agenda.btn.multi")}
        </button>
      )}
    </div>
  );
}
